import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  COUNTRY_GROUP_STACK_ZOOM_THRESHOLD,
  IS_VC_COUNTRY_GROUPING_ENABLED,
} from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';
import { useAiScreenAction } from 'contexts/AiScreenActionsContext';
import { extractApiErrorMessage } from 'utils/helpers';

import {
  buildBoardEntities,
} from '../utils/valueChainCardSelectors';
import {
  applyCountryGroupingToNodes,
  applyScenarioViewCountryGroupingToNodes,
  normalizeNestedUnitLayout,
  removeScenarioViewCountryGroupingFromNodes,
} from '../utils/valueChainNodeLayout';
import useValueChainMapAnalyses from './useValueChainMapAnalyses';

import type { Node } from '@xyflow/react';
import type { IScenarioFormInitialData, IScenarioFormSubmitData } from 'components/AddScenarioSidebar/interfaces/IAddScenarioSidebar';
import type IValueChainMapAnalysisItem from 'store/slices/valueChainMap/interfaces/IValueChainMapAnalysisItem';
import type { TAiScreenAction } from 'utils/interfaces/IAiScreenAction';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type {
  IUseValueChainScenarioViewParams,
  IUseValueChainScenarioViewReturn,
} from '../interfaces/IUseValueChainScenarioView';

const useValueChainScenarioView = ({
  valueChainMapId,
  nodes,
  boardData,
  countryLookup,
  groupedDataRef,
  viewportZoomRef,
  setNodes,
  setEdges,
  closeAIFlow,
  onOpenFilters,
  setOpenFilters,
  setOpenAIGeneratingModal,
  setOpenAICreatorBoMUnits,
  setOpenAICreatorSupplierUnits,
}: IUseValueChainScenarioViewParams): IUseValueChainScenarioViewReturn => {
  const { t } = useTranslation();
  const groupedData = groupedDataRef;
  const [openImpactScenariosSidebar, setOpenImpactScenariosSidebar] = useState<boolean>(false);
  const [selectedImpactAnalysis, setSelectedImpactAnalysis] = useState<
  IValueChainMapAnalysisItem | undefined
  >(undefined);
  // Bumped on every explicit "open this analysis" request — the list, the assistant, a
  // "show in panel" button — even a repeat one for the analysis already showing, so the
  // sidebar's own tab-reset effect has something to key on beyond `analysis?.id`, which a
  // same-analysis reopen would never change.
  const [openRequestId, setOpenRequestId] = useState(0);
  // The run the most recent open request wants pointed out, if any — set once per request and
  // left alone until the next one. Unlike `followedResultId` (in `useValueChainMapAnalyses`),
  // nothing reverts this on its own once the run turns out to be done, so it stays a reliable
  // target for the results tab to scroll to and highlight even if that takes a slow-loading
  // panel several data updates to catch up to.
  const [openResultId, setOpenResultId] = useState<number | null>(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
  const [pendingScenarioInitialData, setPendingScenarioInitialData] = useState<
  IScenarioFormInitialData | null
  >(null);
  const wasScenarioViewModeRef = useRef(false);
  const scenarioViewNodesSnapshotRef = useRef<Node[] | undefined>(undefined);

  const {
    analyses: impactScenarios,
    isLoading: isImpactScenariosLoading,
    isCreateLoading: isImpactScenarioCreating,
    isDeleteLoading: isImpactScenariosDeleting,
    isUpdateLoading: isImpactScenarioUpdating,
    isStartAnalysisLoading: isStartImpactAnalysisLoading,
    isCreateScenarioLoading: isCreateScenarioSaving,
    isCopyAnalysisResultLoading,
    isCopyAnalysisScenariosLoading,
    isDeleteAnalysisResultLoading,
    handleCreateAnalysis: createImpactScenario,
    handleDeleteAnalysis: onDeleteImpactScenario,
    handleUpdateAnalysis: updateImpactScenario,
    handleStartAnalysis: startImpactAnalysis,
    followedResultId,
    handleFollowAnalysisResult,
    handleCreateScenario,
    handleUpdateScenario: updateAnalysisScenario,
    handleDeleteAnalysisScenario,
    handleCopyAnalysisResult,
    handleCopyAnalysisScenarios,
    handleDeleteAnalysisResult,
    valueChainMapAnalysisById,
    isAnalysisDetailsLoading,
    valueChainMapAnalysisResults,
    isAnalysisResultsLoading,
    hasMoreAnalysisResults,
    handleLoadAnalysisResults,
    handleLoadMoreAnalysisResults,
    valueChainMapAnalysisScenarios,
    isAnalysisScenariosLoading,
    hasMoreAnalysisScenarios,
    handleLoadAnalysisScenarios,
    handleLoadMoreAnalysisScenarios,
    valueChainMapAnalysisScenarioDownstream,
    activeScenarioId,
    handleSetActiveScenarioId,
    valueChainMapAnalysisResultDownstream,
    activeResultId,
    handleSetActiveResultId,
  } = useValueChainMapAnalyses({
    valueChainMapId,
    selectedAnalysisId: selectedImpactAnalysis?.id,
  });

  const isScenarioViewMode = Boolean(
    (activeScenarioId && valueChainMapAnalysisScenarioDownstream)
    || (activeResultId && valueChainMapAnalysisResultDownstream),
  );
  const isScenarioViewActive = Boolean(activeScenarioId) && Boolean(valueChainMapAnalysisScenarioDownstream);
  const isResultViewActive = Boolean(activeResultId) && Boolean(valueChainMapAnalysisResultDownstream);
  const activeAnalysisDownstream = isResultViewActive
    ? valueChainMapAnalysisResultDownstream
    : valueChainMapAnalysisScenarioDownstream;
  const highlightedScenarioUnitNodeIds = useMemo<ReadonlySet<string>>(() => (
    new Set((activeAnalysisDownstream?.unitIds || []).map((unitId) => `unit_${unitId}`))
  ), [activeAnalysisDownstream?.unitIds]);

  const boardEntities = useMemo(
    () => buildBoardEntities(nodes, countryLookup),
    [countryLookup, nodes],
  );

  const handleOpenFilters = useCallback(() => {
    setOpenImpactScenariosSidebar(false);
    setSelectedImpactAnalysis(undefined);
    onOpenFilters();
  }, [onOpenFilters]);

  const onResetImpactScenariosView = useCallback(() => {
    setOpenImpactScenariosSidebar(false);
    setSelectedImpactAnalysis(undefined);
  }, []);

  const onOpenImpactScenariosSidebar = useCallback(() => {
    setOpenImpactScenariosSidebar(true);
    setSelectedImpactAnalysis(undefined);
    setOpenFilters(false);
    closeAIFlow();
    setOpenAIGeneratingModal(false);
    setOpenAICreatorBoMUnits(false);
    setOpenAICreatorSupplierUnits(false);
  }, [
    closeAIFlow,
    setOpenAICreatorBoMUnits,
    setOpenAICreatorSupplierUnits,
    setOpenAIGeneratingModal,
    setOpenFilters,
  ]);

  const onCloseImpactScenariosSidebar = useCallback(() => {
    setOpenImpactScenariosSidebar(false);
  }, []);

  const handleCreateImpactScenario = useCallback(async () => {
    try {
      const analysis = await createImpactScenario();

      if (!analysis) return;

      setSelectedImpactAnalysis(analysis);
      setOpenImpactScenariosSidebar(false);
    } catch {
      toast.error(t('common.unknownError'));
    }
  }, [createImpactScenario, t]);

  const handleOpenImpactAnalysis = useCallback((analysis: IValueChainMapAnalysisItem) => {
    setSelectedImpactAnalysis(analysis);
    setOpenImpactScenariosSidebar(false);
    setOpenRequestId((current) => current + 1);
    setOpenResultId(null);
  }, []);

  const handleRenameImpactAnalysis = useCallback(async (
    scenarioValueChainMapId: number,
    id: number,
    name: string,
  ) => {
    const updatedAnalysis = await updateImpactScenario(scenarioValueChainMapId, id, name);

    setSelectedImpactAnalysis(updatedAnalysis);

    return updatedAnalysis;
  }, [updateImpactScenario]);

  const handleBackToImpactScenarios = useCallback(() => {
    setSelectedImpactAnalysis(undefined);
    setOpenImpactScenariosSidebar(true);
  }, []);

  const handleSelectScenario = useCallback((id: number | null) => {
    if (id !== selectedScenarioId) {
      handleSetActiveScenarioId(null);
    }
    setSelectedScenarioId(id);
  }, [handleSetActiveScenarioId, selectedScenarioId]);

  const handleViewScenario = useCallback((id: number) => {
    handleSetActiveResultId(null);
    handleSetActiveScenarioId(activeScenarioId === id ? null : id);
  }, [activeScenarioId, handleSetActiveResultId, handleSetActiveScenarioId]);

  const handleViewResult = useCallback((id: number) => {
    handleSetActiveScenarioId(null);
    handleSetActiveResultId(activeResultId === id ? null : id);
  }, [activeResultId, handleSetActiveResultId, handleSetActiveScenarioId]);

  useEffect(() => {
    setSelectedScenarioId(null);
    handleSetActiveScenarioId(null);
  }, [handleSetActiveScenarioId, selectedImpactAnalysis?.id]);

  const applyScenarioViewStateToNodes = useCallback((nodesToDecorate: Node[]): Node[] => {
    const isViewActive = isScenarioViewActive || isResultViewActive;

    let hasChanges = false;
    const nextNodes = nodesToDecorate.map((node) => {
      if (
        node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE
        || node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
      ) {
        const nodeData = node.data as Record<string, unknown>;
        if (nodeData.isScenarioViewMode === isViewActive) return node;
        hasChanges = true;
        return {
          ...node,
          data: { ...nodeData, isScenarioViewMode: isViewActive },
        };
      }

      if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return node;
      const nodeData = node.data as unknown as ICustomUnitNodeData;
      const isHighlighted = isViewActive && highlightedScenarioUnitNodeIds.has(node.id);

      if (nodeData.isScenarioViewMode === isViewActive && nodeData.isScenarioHighlighted === isHighlighted) {
        return node;
      }

      hasChanges = true;
      return {
        ...node,
        data: {
          ...nodeData,
          isScenarioViewMode: isViewActive,
          isScenarioHighlighted: isHighlighted,
        } as unknown as Record<string, unknown>,
      };
    });

    return hasChanges ? nextNodes : nodesToDecorate;
  }, [
    highlightedScenarioUnitNodeIds,
    isResultViewActive,
    isScenarioViewActive,
  ]);

  useEffect(() => {
    setNodes((prevNodes) => applyScenarioViewStateToNodes(prevNodes));
  }, [applyScenarioViewStateToNodes, boardData, setNodes]);

  useEffect(() => {
    const isViewActive = isScenarioViewActive || isResultViewActive;
    const highlightedConnectionIds = new Set(activeAnalysisDownstream?.connectionIds || []);

    setEdges((prevEdges) => {
      let hasChanges = false;
      const nextEdges = prevEdges.map((edge) => {
        const edgeData = edge.data as Record<string, unknown> | undefined;
        const connection = edgeData?.connection as { id?: number } | undefined;
        const connectionId = connection?.id;
        const isHighlighted = isViewActive && connectionId !== undefined
          && highlightedConnectionIds.has(connectionId);

        if (edgeData?.isScenarioViewMode === isViewActive
          && edgeData?.isScenarioHighlighted === isHighlighted) {
          return edge;
        }

        hasChanges = true;
        return {
          ...edge,
          data: {
            ...edgeData,
            isScenarioViewMode: isViewActive,
            isScenarioHighlighted: isHighlighted,
          },
        };
      });

      return hasChanges ? nextEdges : prevEdges;
    });
  }, [
    activeAnalysisDownstream?.connectionIds,
    boardData,
    isResultViewActive,
    isScenarioViewActive,
    setEdges,
  ]);

  useEffect(() => {
    if (!isScenarioViewMode) {
      if (!wasScenarioViewModeRef.current) {
        return;
      }

      wasScenarioViewModeRef.current = false;
      const scenarioViewNodesSnapshot = scenarioViewNodesSnapshotRef.current;
      scenarioViewNodesSnapshotRef.current = undefined;

      setNodes((prevNodes) => {
        const restoredNodes = scenarioViewNodesSnapshot
          || removeScenarioViewCountryGroupingFromNodes(prevNodes);
        const normalizedNodes = normalizeNestedUnitLayout(restoredNodes);
        const nodesWithoutScenarioState = applyScenarioViewStateToNodes(normalizedNodes);

        if (IS_VC_COUNTRY_GROUPING_ENABLED && viewportZoomRef.current <= COUNTRY_GROUP_STACK_ZOOM_THRESHOLD) {
          return applyCountryGroupingToNodes(nodesWithoutScenarioState);
        }

        groupedData.current = undefined;
        return nodesWithoutScenarioState;
      });

      return;
    }

    wasScenarioViewModeRef.current = true;
    setNodes((prevNodes) => {
      const baseNodes = scenarioViewNodesSnapshotRef.current || normalizeNestedUnitLayout(prevNodes);
      scenarioViewNodesSnapshotRef.current = baseNodes;
      const nodesWithScenarioGroups = IS_VC_COUNTRY_GROUPING_ENABLED
        ? applyScenarioViewCountryGroupingToNodes(
          baseNodes,
          highlightedScenarioUnitNodeIds,
        )
        : baseNodes;

      return applyScenarioViewStateToNodes(nodesWithScenarioGroups);
    });
  }, [
    applyScenarioViewStateToNodes,
    groupedData,
    highlightedScenarioUnitNodeIds,
    isScenarioViewMode,
    setNodes,
    viewportZoomRef,
  ]);

  const handleCreateImpactScenarioFromMonitoring = useCallback(async (
    initialData: IScenarioFormInitialData,
  ) => {
    try {
      const analysis = await createImpactScenario();

      if (!analysis) return;

      setPendingScenarioInitialData(initialData);
    } catch {
      toast.error(t('common.unknownError'));
    }
  }, [createImpactScenario, t]);

  const handleClearPendingScenarioInitialData = useCallback(() => {
    setPendingScenarioInitialData(null);
  }, []);

  const handleSaveScenario = useCallback(async (formData: IScenarioFormSubmitData) => {
    if (!selectedImpactAnalysis) return;

    try {
      await handleCreateScenario(selectedImpactAnalysis.id, formData);
      setPendingScenarioInitialData(null);
    } catch (error) {
      toast.error(extractApiErrorMessage(error) || t('common.unknownError'));
      throw error;
    }
  }, [handleCreateScenario, selectedImpactAnalysis, t]);

  const handleUpdateAnalysisScenario = useCallback(async (
    scenarioId: number,
    formData: IScenarioFormSubmitData,
  ) => {
    if (!selectedImpactAnalysis) return;

    try {
      await updateAnalysisScenario(selectedImpactAnalysis.id, scenarioId, formData);
    } catch (error) {
      toast.error(extractApiErrorMessage(error) || t('common.unknownError'));
      throw error;
    }
  }, [selectedImpactAnalysis, t, updateAnalysisScenario]);

  const onDeleteAnalysisScenario = useCallback(async (scenarioId: number) => {
    if (!selectedImpactAnalysis) return;

    try {
      await handleDeleteAnalysisScenario(selectedImpactAnalysis.id, scenarioId);
    } catch {
      toast.error(t('common.unknownError'));
    }
  }, [handleDeleteAnalysisScenario, selectedImpactAnalysis, t]);

  const handleStartImpactAnalysis = useCallback(async () => {
    if (!selectedImpactAnalysis) return;

    try {
      await startImpactAnalysis(selectedImpactAnalysis.id);
    } catch (error) {
      toast.error(t('common.unknownError'));
      throw error;
    }
  }, [selectedImpactAnalysis, startImpactAnalysis, t]);

  // The assistant opens the analysis panel the way a click in the list does. A run it started
  // is followed here as one the button started: the same list, polling and socket listener.
  useAiScreenAction((action: TAiScreenAction): boolean => {
    if (action.type !== 'openImpactAnalysis' || action.valueChainMapId !== Number(valueChainMapId)) {
      return false;
    }

    onOpenImpactScenariosSidebar();
    handleOpenImpactAnalysis({
      id: action.analysisId, valueChainMapId: action.valueChainMapId, name: action.name,
    });

    setOpenResultId(action.resultId ?? null);

    if (action.resultId != null) {
      handleFollowAnalysisResult(action.analysisId, action.resultId, action.view);
    }

    return true;
  });

  return {
    boardEntities,
    isScenarioViewMode,
    openImpactScenariosSidebar,
    selectedImpactAnalysis,
    impactScenarios,
    isImpactScenariosLoading,
    isImpactScenarioCreating,
    isImpactScenariosDeleting,
    isImpactScenarioUpdating,
    isCreateScenarioSaving,
    valueChainMapAnalysisById,
    isAnalysisDetailsLoading,
    valueChainMapAnalysisResults,
    isAnalysisResultsLoading,
    hasMoreAnalysisResults,
    valueChainMapAnalysisScenarios,
    isAnalysisScenariosLoading,
    hasMoreAnalysisScenarios,
    isCopyAnalysisResultLoading,
    isCopyAnalysisScenariosLoading,
    isDeleteAnalysisResultLoading,
    isStartImpactAnalysisLoading,
    selectedScenarioId,
    activeScenarioId,
    activeResultId,
    followedResultId,
    openRequestId,
    openResultId,
    onOpenFilters: handleOpenFilters,
    onResetImpactScenariosView,
    onOpenImpactScenariosSidebar,
    onCloseImpactScenariosSidebar,
    onBackToImpactScenariosSidebar: handleBackToImpactScenarios,
    onCreateImpactScenario: handleCreateImpactScenario,
    onOpenImpactAnalysis: handleOpenImpactAnalysis,
    onRenameImpactAnalysis: handleRenameImpactAnalysis,
    onDeleteImpactScenario,
    onSaveScenario: handleSaveScenario,
    onUpdateScenario: handleUpdateAnalysisScenario,
    onDeleteAnalysisScenario,
    onLoadAnalysisResults: handleLoadAnalysisResults,
    onLoadMoreAnalysisResults: handleLoadMoreAnalysisResults,
    onLoadAnalysisScenarios: handleLoadAnalysisScenarios,
    onLoadMoreAnalysisScenarios: handleLoadMoreAnalysisScenarios,
    onCopyAnalysisResult: handleCopyAnalysisResult,
    onCopyAnalysisScenarios: handleCopyAnalysisScenarios,
    onDeleteAnalysisResult: handleDeleteAnalysisResult,
    onStartImpactAnalysis: handleStartImpactAnalysis,
    onSelectScenario: handleSelectScenario,
    onViewScenario: handleViewScenario,
    onViewResult: handleViewResult,
    pendingScenarioInitialData,
    onCreateImpactScenarioFromMonitoring: handleCreateImpactScenarioFromMonitoring,
    onClearPendingScenarioInitialData: handleClearPendingScenarioInitialData,
  };
};

export default useValueChainScenarioView;
